import { useMemo } from "react";
import { useTranslation } from "react-i18next";

function getStepState({
  stepIndex,
  activeStep,
  stepComplete,
  wizardPath,
  isCollapsed,
  visualIndex,
  activeVisualIndex,
  isChoosingPath,
}) {
  const isComplete = !!stepComplete[stepIndex] || stepIndex < activeStep;
  const isActive = visualIndex === activeVisualIndex && !isChoosingPath;
  const isLocked = isCollapsed && visualIndex === 1 && !wizardPath;
  const isFuture = !isComplete && !isActive && stepIndex > activeStep;

  if (isChoosingPath && visualIndex === 0 && isComplete) return "done";
  if (isLocked) return "locked";
  if (isActive) return "active";
  if (isComplete) return "done";
  if (isFuture) return "upcoming";
  return "upcoming";
}

function canNavigateToStep({ stepIndex, activeStep, stepComplete, wizardPath, isCollapsed, visualIndex }) {
  if (isCollapsed) {
    if (!wizardPath && visualIndex === 1) return false;
    return visualIndex === 0 || stepIndex <= activeStep;
  }
  return stepIndex <= activeStep || !!stepComplete[stepIndex];
}

const WizardStepper = ({
  steps,
  activeStep,
  activeVisualIndex,
  stepComplete,
  wizardPath,
  isChoosingPath = false,
  onStepClick,
  totalSteps,
}) => {
  const { t } = useTranslation();
  const isCollapsed = !wizardPath && steps.length === 2;

  const progressPct = useMemo(() => {
    if (steps.length <= 1) return 0;
    const maxIdx = steps.length - 1;
    if (isChoosingPath) return 50;
    const current = Math.min(Math.max(activeVisualIndex, 0), maxIdx);
    return (current / maxIdx) * 100;
  }, [steps.length, activeVisualIndex, isChoosingPath]);

  const currentLabel = isChoosingPath
    ? t("wizardStepChoosePath")
    : steps[activeVisualIndex]?.label || "";

  return (
    <div
      className="wizard-stepper-v2"
      data-collapsed={isCollapsed ? "true" : "false"}
      data-choosing={isChoosingPath ? "true" : "false"}
    >
      <div className="wizard-stepper-v2__meta">
        <span className="wizard-stepper-v2__eyebrow">
          {t("wizardStepperEyebrow", { current: activeStep + 1, total: totalSteps })}
        </span>
        <strong className="wizard-stepper-v2__current">{currentLabel}</strong>
      </div>

      <div className="wizard-stepper-v2__scroll">
        <div className="wizard-stepper-v2__track">
          <div
            className="wizard-stepper-v2__rail"
            aria-hidden="true"
            style={{ "--wizard-step-progress": `${progressPct}%` }}
          />

          {steps.map((step, visualIndex) => {
            const state = getStepState({
              stepIndex: step.stepIndex,
              activeStep,
              stepComplete,
              wizardPath,
              isCollapsed,
              visualIndex,
              activeVisualIndex,
              isChoosingPath,
            });
            const clickable = canNavigateToStep({
              stepIndex: step.stepIndex,
              activeStep,
              stepComplete,
              wizardPath,
              isCollapsed,
              visualIndex,
            });
            const showConnector = visualIndex < steps.length - 1;
            const connectorFilled = isChoosingPath || state === "done";

            return (
              <div
                key={`${step.label}-${step.stepIndex}`}
                className={`wizard-stepper-v2__unit wizard-stepper-v2__unit--${state}`}
              >
                <button
                  type="button"
                  className="wizard-stepper-v2__node"
                  title={step.label}
                  disabled={!clickable}
                  aria-current={state === "active" ? "step" : undefined}
                  onClick={() => clickable && onStepClick(step.stepIndex)}
                >
                  <span className="wizard-stepper-v2__circle">
                    {state === "done" ? (
                      <i className="fa fa-check" aria-hidden="true" />
                    ) : step.icon ? (
                      <i className={step.icon} aria-hidden="true" />
                    ) : (
                      <span>{visualIndex + 1}</span>
                    )}
                  </span>
                  <span className="wizard-stepper-v2__label">{step.label}</span>
                </button>

                {showConnector ? (
                  <div
                    className={`wizard-stepper-v2__connector${
                      connectorFilled ? " is-filled" : ""
                    }${isCollapsed ? " is-collapsed" : ""}${isChoosingPath ? " is-choosing" : ""}`}
                    aria-hidden="true"
                  >
                    {isCollapsed ? (
                      <span className="wizard-stepper-v2__connector-dots">
                        <span />
                        <span />
                        <span />
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WizardStepper;
